import React, {useEffect, useState} from 'react';
import {RHButton} from 'src/common-components/custom-ui-helpers';
import Spinner from 'react-native-loading-spinner-overlay';
import * as Yup from 'yup';
import {InputGroup, Modal} from 'src/design-system';
import Form from 'src/hook-form/form';
import {FormProvider, useFieldArray, useForm, useWatch} from 'react-hook-form';
import {useDatabase} from '@nozbe/watermelondb/react';
import {useSelector} from 'react-redux';
import moment from 'moment';
import {useStyle} from 'src/providers/style';
import {useTranslations} from 'src/providers/translation';
import Client from 'src/hook-form-inputs/client';
import Location from 'src/hook-form-inputs/location';
import NoteTemplate from 'src/hook-form-inputs/note-template';
import Staff from 'src/hook-form-inputs/staff';
import {yupResolver} from '@hookform/resolvers/yup';
import {
  Appointment,
  NoteTemplateVersion,
  Participant,
  Session,
} from 'src/models';
import {Q} from '@nozbe/watermelondb';
import Address from 'src/hook-form-inputs/address';
import AddressAutocompleteInput from 'src/hook-form-wrapper/address-input';
import UserListItem from '../user-list-item';
import StartTime from 'src/hook-form-inputs/start-time';
import EndTime from 'src/hook-form-inputs/end-time';
import {Separator} from 'src/common-components/atoms';
import _ from 'lodash';
import {Platform} from 'react-native';

const roundedUp = Math.ceil(moment().minute() / 15) * 15;

const QuickSession = ({navigation}: any) => {
  const database = useDatabase();
  const styles = useStyle();
  const translations = useTranslations();

  const {selectedGroup, userId} = useSelector(state => state.authentication);
  const isWeb = Platform.OS === 'web';

  const [loading, setLoading] = useState(false);
  const [show, setShow] = useState(false);

  const navigateBack = (values: any) => {
    methods.reset();
    setShow(false);
    navigation.navigate('ScheduleAppointment', {
      location: values.location,
      patient: values.clients,
      address: values.address,
      noteTemplate: values.noteTemplate,
      startTime: values.startTime,
      endTime: values.endTime,
      staff: values.staff,
      selectedStaff: values.selectedStaff,
    });
  };

  const initialValues = {
    clients: '',
    staff: [userId],
    selectedStaff: [],
    location: '',
    noteTemplate: '',
    address: '',
    cpt: [],
    startTime: moment().minute(roundedUp).second(0).toDate(),
    endTime: moment().minute(roundedUp).second(0).add(60, 'minutes').toDate(),
  };

  const validationSchema = Yup.object({
    clients: Yup.string().required('Client is required'),
    staff: Yup.array()
      .min(1, 'At least one Staff member is required.')
      .required('Staff selection is required'),
    location: Yup.string().required('Place of Service is required'),
    noteTemplate: Yup.string().required('Note Template is required.'),
    startTime: Yup.date().required('Start Time is required.'),
    endTime: Yup.date()
      .required('End Time is required.')
      .test('after', 'End Time must be after Start Time.', function (value) {
        const appStart = this.parent.startTime;
        const startDate = new Date(
          value.getFullYear(),
          value.getMonth(),
          value.getDate(),
          appStart.getHours(),
          appStart.getMinutes(),
        );
        const endDate = new Date(
          value.getFullYear(),
          value.getMonth(),
          value.getDate(),
          value.getHours(),
          value.getMinutes(),
        );
        return moment(endDate).isSameOrAfter(moment(startDate));
      }),
    selectedStaff: Yup.array()
      .of(
        Yup.object().shape({
          startTime: Yup.date()
            .test(
              'is-within-appointment-start',
              'Staff Start Time must be within Appointment times.',
              function (value) {
                const valueDate = moment(
                  moment(value).format('HH:mm:ss'),
                  'HH:mm:ss',
                );
                const startDate = moment(
                  moment(startTime).format('HH:mm:ss'),
                  'HH:mm:ss',
                );
                const endDate = moment(
                  moment(endTime).format('HH:mm:ss'),
                  'HH:mm:ss',
                );

                return (
                  valueDate.isSameOrAfter(startDate, 'minutes') &&
                  valueDate.isSameOrBefore(endDate, 'minutes')
                );
              },
            )
            .required('Staff Start Time is required.'),
          endTime: Yup.date()
            .test(
              'is-after-start-time',
              'Staff End Time must be after Staff Start Time.',
              function (value) {
                const valueDate = moment(
                  moment(value).format('HH:mm:ss'),
                  'HH:mm:ss',
                );
                const startDate = moment(
                  moment(startTime).format('HH:mm:ss'),
                  'HH:mm:ss',
                );

                return valueDate.isSameOrAfter(startDate, 'minutes');
              },
            )
            .test(
              'is-within-appointment-end',
              'Staff End Time must be within Appointment times.',
              function (value) {
                const valueDate = moment(
                  moment(value).format('HH:mm:ss'),
                  'HH:mm:ss',
                );
                const startDate = moment(
                  moment(startTime).format('HH:mm:ss'),
                  'HH:mm:ss',
                );
                const endDate = moment(
                  moment(endTime).format('HH:mm:ss'),
                  'HH:mm:ss',
                );

                return (
                  valueDate.isSameOrAfter(startDate, 'minutes') &&
                  valueDate.isSameOrBefore(endDate, 'minutes')
                );
              },
            )
            .required('Staff End Time is required.'),
          supervision: Yup.boolean().test(
            'supervision',
            'Supervision appointments should have at least one non-supervisor for data collection.',
            () => {
              const selectedStaff = [...methods.getValues().selectedStaff];
              return selectedStaff?.some(value => !value.supervision);
            },
          ),
        }),
      )
      .required('Staff information is required'),
  });

  const methods = useForm({
    defaultValues: initialValues,
    resolver: yupResolver(validationSchema),
  });

  const patientId = methods.watch('clients');
  const users = useWatch({control: methods.control, name: 'staff'});
  const watchedStaff = useWatch({
    control: methods.control,
    name: 'selectedStaff',
  });
  const startTime = useWatch({control: methods.control, name: 'startTime'});
  const endTime = useWatch({control: methods.control, name: 'endTime'});

  const {remove: userRemove} = useFieldArray({
    name: 'staff',
    control: methods.control,
  });

  useEffect(() => {
    if (users.length === 1) {
      const selectedStaff = [
        {
          ...methods.getValues().selectedStaff[0],
          startTime,
          endTime,
        },
      ];

      methods.setValue('selectedStaff', selectedStaff);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [users, startTime, endTime]);

  useEffect(() => {
    const selectedStaff = watchedStaff;
    const deleteStaff = _.differenceWith(
      selectedStaff,
      users,
      (value1: any, value2: string) => {
        return value1.id === value2;
      },
    );
    const differenceStaff = _.differenceWith(
      users,
      selectedStaff,
      (value1: string, value2: any) => {
        return value1 === value2.id;
      },
    );
    for (const staffMember of deleteStaff) {
      const index = selectedStaff.findIndex(
        (member: any) => member.id === staffMember.id,
      );

      selectedStaff.splice(index, 1);
    }
    for (const staffMember of differenceStaff) {
      selectedStaff.push({
        id: staffMember,
        billable: true,
        supervision: false,
        startTime: methods.getValues().startTime,
        endTime: methods.getValues().endTime,
        cpt: [],
      });
    }
    methods.setValue('selectedStaff', selectedStaff);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [methods, users]);

  const handleSubmit = async (values: any) => {
    setLoading(true);
    let noteTemplate: any;
    const noteTemplateVersions = await database
      .get(NoteTemplateVersion.table)
      .query(
        Q.where('note_template_id', values.noteTemplate),
        Q.sortBy('published_at', 'desc'),
        Q.take(1),
      );
    if (noteTemplateVersions.length) {
      noteTemplate = noteTemplateVersions[0];
    }
    const startDate = new Date();
    const apptTime = new Date(values.startTime);
    const apptDate = new Date(
      startDate.getFullYear(),
      startDate.getMonth(),
      startDate.getDate(),
      apptTime.getHours(),
      apptTime.getMinutes(),
      0,
      0,
    );
    let finalizedSession = await database?.write(async () => {
      const createAppointment = await database
        ?.get(Appointment.table)
        .create(createdAppointment => {
          createdAppointment.partition = selectedGroup;
          createdAppointment.location = values.location;
          createdAppointment.address = values.address;
          createdAppointment.customAddress = values.customAddress;
          createdAppointment.date = apptDate;
          createdAppointment.start = values.startTime;
          createdAppointment.end = values.endTime;
          createdAppointment.noteTemplateId = noteTemplate.noteTemplateId;
          createdAppointment.createdBy = userId;
          createdAppointment.updatedBy = userId;
        });

      for (const staffParticipant of values.selectedStaff) {
        await database?.get(Participant.table).create(entity => {
          entity.partition = selectedGroup;
          entity.billable = staffParticipant.billable;
          entity.supervision = staffParticipant.supervision;
          entity.appointment.id = createAppointment.id;
          entity.user.id = staffParticipant.id;
          entity.startTime =
            values.selectedStaff.length === 1
              ? values.startTime
              : staffParticipant.startTime;
          entity.endTime =
            values.selectedStaff.length === 1
              ? values.endTime
              : staffParticipant.endTime;
          entity.cpt = staffParticipant.cpt;
        });
      }
      await database?.get(Participant.table).create(entity => {
        entity.partition = selectedGroup;
        entity.startTime = values.startTime;
        entity.endTime = values.endTime;
        entity.appointment.id = createAppointment.id;
        entity.patient.id = values.clients;
        entity.createdBy = userId;
        entity.updatedBy = userId;
      });
      return await database?.get(Session.table).create(entity => {
        entity.partition = selectedGroup;
        entity.type = 'session';
        entity.date = new Date();
        entity.sessionDate = moment().format('YYYY-MM-DD');
        entity.startTimestamp = null;
        entity.endTimestamp = null;
        entity.editedStartTimestamp = null;
        entity.editedEndTimestamp = null;
        entity.note = noteTemplate.template;
        entity.noteTemplateId = values.noteTemplate;
        entity.noteTemplateVersionId = noteTemplate.id;
        entity.address = values.address;
        entity.customAddress = values.customAddress;
        entity.additionalPostalCode = values.additionalPostalCode;
        entity.therapistSignature = null;
        entity.therapistSignature2 = null;
        entity.parentSignature1 = null;
        entity.parentSignature2 = null;
        entity.attestationTimestamp = null;
        entity.submissionTimestamp = null;
        entity.appointment.id = createAppointment.id;
        entity.patient.id = values.clients;
        entity.user.id = null;
        entity.createdBy = userId;
        entity.updatedBy = userId;
      });
    });
    setLoading(false);
    setShow(false);
    methods.reset();
    navigation.navigate('SessionCollect', {
      id: finalizedSession.id,
    });
  };
  const location = useWatch({
    control: methods.control,
    name: 'location',
  });
  const watchedAddress = useWatch({
    control: methods.control,
    name: 'address',
  });

  return (
    <>
      <Modal show={[show, setShow]} title={translations('create_session')}>
        <Spinner visible={loading} />
        <FormProvider {...methods}>
          <Form
            handleSubmit={methods.handleSubmit(handleSubmit)}
            cancelled={() => navigateBack(methods.getValues())}
            cancelText={translations('schedule_later')}
            saveText={translations('schedule_now')}>
            <>
              <Staff multiple={true} />
              {watchedStaff.map((item: any, index: number) => (
                <UserListItem
                  key={`staff-list-item-${item?.id}`}
                  id={item.id}
                  index={index}
                  showTime={watchedStaff.length > 1}
                  onClose={() => {
                    userRemove(index);
                  }}
                />
              ))}
              <Client multiple={false} />
              <InputGroup
                orientation={isWeb ? 'container' : 'inputGroupMobile'}
                left={
                  <>
                    <StartTime date={startTime} />
                  </>
                }
                right={
                  <>
                    <EndTime date={startTime} />
                  </>
                }
              />
              <Separator height={20} />
              <Location />
              <Address
                patientId={
                  location === '12' || location === '10' ? patientId : undefined
                }
                combineLocations={location === '10' && patientId}
              />
              {watchedAddress === 'other' ? (
                <AddressAutocompleteInput
                  name={'customAddress'}
                  label={'Address Line 1'}
                />
              ) : null}
              <NoteTemplate />
            </>
          </Form>
        </FormProvider>
      </Modal>
      <RHButton
        style={[styles.marginTop]}
        onPress={() => setShow(true)}
        mode={'contained'}
        secondary>
        {translations('create_new_session')}
      </RHButton>
    </>
  );
};

export default QuickSession;
